import React, { createContext, useContext } from 'react';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { useFormWatcher } from 'src/components/oneCorp/features/MealCredit/components/Form/hooks/useFormWatcher';
import { useInitializeForm } from 'src/components/oneCorp/features/MealCredit/components/Form/hooks/useInitializeForm';
import { useMealCreditData } from 'src/components/oneCorp/features/MealCredit/components/Form/hooks/useMealCreditData';
import { IMealCredit } from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseMealCreditsDetailsPage.utils';
import { MealCreditDataContextProps } from 'src/components/oneCorp/features/MealCredit/components/Form/MealCreditDataProvider/MealCreditDataProvider.utils';

export const MealCreditDataContext =
  createContext<MealCreditDataContextProps | null>(null);

export function MealCreditDataProvider({
  children,
  mealCredit = null,
}: {
  children: React.ReactNode;
  mealCredit?: IMealCredit;
}): JSX.Element {
  const { corpData, corpId } = useAuth();
  const isOrderPolicyVisible = !!corpData?.orderPolicyId;
  const expenseCodesForLabel = corpData?.expenseCodeAliasPlural;
  const groupOrderEnabled = corpData?.groupOrderEnabled;

  const { initForm } = useInitializeForm();

  const { setFormFieldsWithMealCreditData } = useMealCreditData({
    corpData,
  });

  const isEditing = !!mealCredit;

  const {
    budgetLength,
    budgetResetPeriod,
    startTime,
    startTimeMeridian,
    endTime,
    endTimeMeridian,
    date,
    startDate,
    endTimeFullDate,
    isDailyLengthBudget,
    isWeeklyLengthBudget,
    isMultiDaysLengthBudget,
    isCustomLengthBudget,
    isRepeating,
    hasLocation,
    expirationDate,
    selectedDays,
    isTimeInputDisabled,
    timeIntervalType,
    expiresOn,
    budgetResetType,
    numberOfConsecutiveDays,
    numberOfConsecutiveMonths,
    isMonthlyLengthBudget,
    isMonthAvailable,
    isMonthAvailableEveryDay,
    isMonthAvailableOnSpecificDay,
    isMonthAvailableWithinDateRange,
    isDaysAvailable,
    monthAvailableOn,
    monthAvailableTo,
  } = useFormWatcher({ isEditingMealCredit: isEditing });

  const contextValue: MealCreditDataContextProps = {
    isEditing,
    mealCredit,
    formActions: {
      initForm,
      setFormFieldsWithMealCreditData,
    },
    formValues: {
      numberOfConsecutiveDays,
      numberOfConsecutiveMonths,
      budgetLength,
      budgetResetPeriod,
      date,
      startTime,
      startTimeMeridian,
      endTime,
      endTimeMeridian,
      startDate,
      endTimeFullDate,
      isDailyLengthBudget,
      isWeeklyLengthBudget,
      isMonthlyLengthBudget,
      isMultiDaysLengthBudget,
      isCustomLengthBudget,
      isRepeating,
      hasLocation,
      expirationDate,
      selectedDays,
      isTimeInputDisabled,
      timeIntervalType,
      expiresOn,
      budgetResetType,
      isMonthAvailable,
      isMonthAvailableEveryDay,
      isMonthAvailableOnSpecificDay,
      isMonthAvailableWithinDateRange,
      isDaysAvailable,
      monthAvailableOn,
      monthAvailableTo,
    },
    corpDataValues: {
      corpId,
      corpData,
      isOrderPolicyVisible,
      expenseCodesForLabel,
      groupOrderEnabled,
    },
  };

  return (
    <MealCreditDataContext.Provider value={contextValue}>
      {children}
    </MealCreditDataContext.Provider>
  );
}

export function useMealCreditFormData(): MealCreditDataContextProps {
  const context = useContext(MealCreditDataContext);
  if (context === null) {
    throw new Error(
      'useMealCreditFormData must be used within a MealCreditDataProvider'
    );
  }
  return context;
}
